import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Box, Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import { formatNumber } from "@/features/shared";

/**
 * Maps every discount on the source payment onto one of the hub's own accounts.
 *
 * The amounts are the source's and cannot be changed; only the account each one
 * lands in is the hub's call. The backend requires every row to be mapped
 * exactly once, so the rows are fixed — none can be added or removed.
 */
export const DiscountAccountsSection = () => {
  const { outlet } = useAuth();
  const { control } = useFormContext();
  const { fields } = useFieldArray({ control, name: "discountAccounts" });

  if (fields.length === 0) return null;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">Pemetaan Akun Diskon</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Setiap diskon pada pembayaran asal harus dipetakan ke akun outlet
            ini. Nilainya mengikuti pembayaran asal dan tidak dapat diubah.
          </Typography>
        </Stack>

        <DataGrid
          rows={fields}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "purchaseInvoiceReferenceNumber",
              headerName: "Faktur",
              flex: 2,
              sortable: false,
              valueGetter: (referenceNumber) => referenceNumber || "-",
            },
            {
              field: "sourceOutletChartOfAccount",
              headerName: "Akun Asal",
              flex: 2.5,
              sortable: false,
              valueGetter: (account) =>
                account ? `${account.code} - ${account.name}` : "-",
            },
            {
              field: "amount",
              headerName: "Nilai (Rp)",
              flex: 1,
              type: "number",
              sortable: false,
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "outletChartOfAccount",
              headerName: "Akun Diskon",
              flex: 2.5,
              sortable: false,
              renderCell: ({ id }) => {
                const index = fields.findIndex((field) => field.id === id);
                if (index === -1) return null;

                return (
                  <Box sx={{ width: "100%", py: 1 }}>
                    <Controller
                      control={control}
                      name={`discountAccounts.${index}.outletChartOfAccount`}
                      render={({ field, fieldState }) => (
                        <OutletChartOfAccountSelect
                          value={field.value}
                          onChange={field.onChange}
                          outletId={outlet?.id}
                          TextFieldProps={{
                            required: true,
                            error: fieldState.invalid,
                            helperText: fieldState.error?.message,
                            InputProps: {
                              placeholder: "Pilih Akun Diskon",
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                );
              },
            },
          ]}
          disableRowSelectionOnClick
          hideFooter
          // The picker owns focus inside its cell; the grid's own focus ring on
          // top of the input border reads as a rendering glitch.
          sx={{ "& .MuiDataGrid-cell:focus-within": { outline: "none" } }}
        />
      </Stack>
    </Paper>
  );
};
